import { Button, IconButton, SearchBar } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

import { NoItemFound } from "@/components/shared/NoItemFound";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { VocabCardSkeleton } from "@/components/skeletons";
import { AddVocabCardModal } from "@/components/vocab/AddVocabCardModal";
import { VocabCardComponent } from "@/components/vocab/VocabCardComponent";
import { useResponsiveSkeletonCount } from "@/lib/hooks/useResponsiveSkeletonCount";
import { useDeleteVocabCards } from "@/lib/hooks/vocabCards/useDeleteVocabCards";
import { useVocabCards } from "@/lib/hooks/vocabCards/useVocabCards";
import { useDebouncedSearch } from "@/lib/utils/useDebouncedSearch";
import type { VocabCardFields } from "@/types/VocabCard";
import { HiPlus } from "react-icons/hi";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export function WordBankPage() {
  const [searchInput, setSearchInput] = useState("");
  const [letterFilter, setLetterFilter] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingVocabCard, setIsEditingVocabCard] = useState<VocabCardFields | undefined>(
    undefined
  );
  const skeletonCount = useResponsiveSkeletonCount(2);

  const debouncedSearch = useDebouncedSearch(searchInput);

  // Letter filter takes priority over typed search
  const effectiveSearch = letterFilter ?? (debouncedSearch || null);

  const { deleteVocabCards } = useDeleteVocabCards();

  const { vocabCards, hasMore, loading, error, refetch, loadMore } = useVocabCards({
    searchText: effectiveSearch,
  });

  // Infinite scroll trigger
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  // Automatically load more when trigger comes into view
  useEffect(() => {
    if (inView && hasMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, loading, loadMore]);

  const handleLetterSelect = (letter: string | null) => {
    setLetterFilter(letter);
    setSearchInput("");
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setLetterFilter(null);
  };

  return (
    <div className="flex h-screen">
      <MainSidebar />

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Search Bar */}
          <div className="flex items-center gap-3 mb-4">
            <SearchBar value={searchInput} onChange={handleSearchChange} className="w-1/4" />
          </div>

          {/* Alphabet Filter */}
          <div className="flex flex-wrap items-center gap-1 mb-6">
            <Button
              variant={letterFilter === null && !debouncedSearch ? "primary" : "ghost"}
              size="sm"
              onClick={() => handleLetterSelect(null)}
            >
              All
            </Button>
            {ALPHABET.map((letter) => (
              <Button
                key={letter}
                variant={letterFilter === letter ? "primary" : "ghost"}
                size="sm"
                onClick={() => handleLetterSelect(letter)}
              >
                {letter}
              </Button>
            ))}
          </div>

          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="h2">Vocabulary Cards</h1>
            <IconButton
              aria-label="Add new vocabulary card"
              variant="primary"
              shape="circle"
              size="md"
              onClick={() => {
                setIsAddModalOpen(true);
              }}
            >
              <HiPlus className="w-5 h-5" aria-hidden="true" title="Add Item" />
            </IconButton>
          </div>

          {/* Vocab Cards Grid */}
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {vocabCards.length > 0 ? (
                vocabCards.map((card) => (
                  <VocabCardComponent
                    onEdit={(vocabCard) => {
                      setIsEditingVocabCard(vocabCard);
                      setIsAddModalOpen(true);
                    }}
                    onDelete={(id) => deleteVocabCards([id])}
                    key={card.id}
                    vocabCard={card as VocabCardFields}
                  />
                ))
              ) : loading ? (
                Array.from({ length: skeletonCount }).map((_, i) => (
                  <VocabCardSkeleton key={`vocab-skeleton-${i + 1}`} />
                ))
              ) : (
                <NoItemFound itemType="vocabulary card" error={error} refetch={refetch} />
              )}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={loadMoreRef} className="flex justify-center py-8">
                {loading && (
                  <div className="flex items-center gap-2 text-muted">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span>Loading more vocabulary cards...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isAddModalOpen && (
        <AddVocabCardModal
          vocabCard={editingVocabCard}
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setIsEditingVocabCard(undefined);
          }}
        />
      )}
    </div>
  );
}
